// The valley's bots (gate A2.2), on @rpg/harness's route bot and monkey, driving the hero through the actions
// (`move`, `jump`, `sprint`, `glide`) one sim step at a time while the invariant watch runs after every step:
//   - the route bot walks the course in seeded laps (the sections in a seeded order, with seeded sprints, stick tilts
//     and jumps on the free legs between them) for the minutes asked, and records every leg: reached or blocked as
//     expected, the steps spent off the ground on legs that must stay grounded, the water and sand met;
//   - the monkey holds seeded random inputs (moves, sprints, jumps, glides, idling).
// Both report their seed. Time runs through the runtime's deterministic advance, so a run replays from its seed.
import { createMonkey, createRouteBot, seededRandom } from "@rpg/harness";
import { forwardOf, rightOf } from "@rpg/controller";
import type { Runtime } from "@rpg/runtime";
import { SPAWN, courseSections, type CourseLeg } from "./course";
import type { ValleyRig } from "./scene";

/** One leg's record. */
export interface LegRecord {
  lap: number;
  name: string;
  expect: "reach" | "blocked";
  reached: boolean;
  ok: boolean;
  time: number;
  maxY: number;
  /** Steps spent off the ground (not grounded, or not in the ground mode). */
  airSteps: number;
  grounded: boolean;
  meets: "water" | "sand" | null;
  met: boolean;
  tests: string | null;
}

export interface RouteReport {
  seed: number;
  simSeconds: number;
  laps: number;
  legs: LegRecord[];
  /** Per course element (a step, a ramp): tries and how many reached the top. */
  climbed: Record<string, { tries: number; reached: number; allowed: boolean }>;
  /** Legs that didn't do what they expected, grounded legs that left the ground, legs that missed what they meet. */
  failures: string[];
}

/** Turn a world direction into the stick for the camera's yaw (the inverse of stickToWorld), scaled by `tilt`. */
function stickFor(dir: { x: number; z: number }, cameraYaw: number, tilt: number): { x: number; y: number } {
  const f = forwardOf(cameraYaw);
  const r = rightOf(cameraYaw);
  return { x: (dir.x * r.x + dir.z * r.z) * tilt, y: (dir.x * f.x + dir.z * f.z) * tilt };
}

/** Advance `seconds` of sim time without drawing, in slices of at most a second. Sim time, not real time: since phase
 *  B a hit-stop (the valley's fight) holds the sim while real time runs. */
function advance(runtime: Runtime, seconds: number, until?: () => boolean): void {
  const t0 = runtime.time;
  for (let i = 0; runtime.time - t0 < seconds - 1e-9 && !until?.() && i < seconds * 10 + 100; i++) runtime.advance(Math.min(1, seconds - (runtime.time - t0)) * 1000 + 0.01, { draw: false });
}

/** Run the route bot for `minutes` of sim time (default 10) from the hub. */
export function runRoute(rig: ValleyRig, runtime: Runtime, o: { seed?: number; minutes?: number; laps?: number } = {}): RouteReport {
  const seed = o.seed ?? 20261017;
  const random = seededRandom(seed);
  const sections = courseSections(rig.course);
  const names = Object.keys(sections);
  const legs: LegRecord[] = [];
  const climbed: RouteReport["climbed"] = {};
  for (const s of rig.course.steps) climbed[s.id] = { tries: 0, reached: 0, allowed: s.allowed };
  for (const r of rig.course.ramps) climbed[r.id] = { tries: 0, reached: 0, allowed: r.allowed };
  const t0 = runtime.time;
  const limit = (o.minutes ?? 10) * 60;
  rig.warpHero(SPAWN.x, rig.terrainAt(SPAWN.x, SPAWN.z), SPAWN.z, SPAWN.yaw);
  rig.actions.setStick("move", { x: 0, y: 0 });
  let lap = 0;
  let bot = createRouteBot<CourseLeg>([], () => rig.hero().feet);
  let air = 0;
  let met = false;
  let tilt = 1;
  let sprint = false;
  let legName = "";

  const newLap = (): void => {
    lap++;
    // the sections in a seeded order (Fisher–Yates)
    const order = [...names];
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [order[i], order[j]] = [order[j]!, order[i]!];
    }
    bot = createRouteBot<CourseLeg>(
      order.flatMap((n) => sections[n]!),
      () => rig.hero().feet,
    );
  };
  newLap();

  rig.drive = () => {
    const s = bot.steer();
    if (!s) {
      rig.actions.setStick("move", { x: 0, y: 0 });
      return;
    }
    if (s.leg.name !== legName) {
      // a new leg: seeded tilt and sprint on the free legs, full tilt on the tested ones
      legName = s.leg.name;
      tilt = s.leg.transit ? 0.75 + 0.25 * random() : 1;
      sprint = s.leg.transit === true && random() < 0.35;
      air = 0;
      met = false;
    }
    // slow down near the target so a leg's end doesn't overshoot into the next one's obstacle
    const k = Math.min(1, 0.35 + s.distance / 2);
    rig.actions.setStick("move", stickFor(s.dir, rig.control().cameraYaw, tilt * k));
    if (sprint) rig.actions.holdAction("sprint", rig.dt * 1.5);
    // a jump carries about 5 m at a sprint: none near a leg's end, so the next (perhaps grounded) leg starts on the ground
    if (s.leg.transit && s.distance > 7 && rig.hero().mode === "ground" && random() < 0.6 * rig.dt) {
      rig.actions.holdAction("jump", 0.2);
    }
  };
  rig.observe = () => {
    const leg = bot.steer()?.leg;
    if (!leg) return;
    const h = rig.hero();
    if (!h.grounded || h.mode !== "ground") air++;
    if (leg.meets === "water" && h.water) met = true;
    if (leg.meets === "sand" && h.surface === "sand") met = true;
    const before = bot.results.length;
    bot.update(rig.dt);
    if (bot.results.length > before) {
      const r = bot.results[bot.results.length - 1]!;
      legs.push({ lap, name: r.name, expect: r.expect, reached: r.reached, ok: r.ok, time: r.time, maxY: r.maxY, airSteps: air, grounded: leg.grounded === true, meets: leg.meets ?? null, met, tests: leg.tests ?? null });
      if (leg.tests) {
        climbed[leg.tests]!.tries++;
        if (r.reached) climbed[leg.tests]!.reached++;
      }
      air = 0;
      met = false;
    }
    if (bot.done && (o.laps === undefined || lap < o.laps)) newLap();
  };
  try {
    advance(runtime, o.laps !== undefined ? 3600 : limit, () => (o.laps !== undefined ? bot.done && lap >= o.laps : false));
  } finally {
    rig.drive = null;
    rig.observe = null;
    rig.actions.setStick("move", null);
  }
  const failures: string[] = [];
  for (const l of legs) {
    if (!l.ok) failures.push(`lap ${l.lap} "${l.name}": ${l.expect === "reach" ? "not reached" : "reached, should be blocked"} (max y ${l.maxY.toFixed(2)})`);
    if (l.grounded && l.airSteps > 0) failures.push(`lap ${l.lap} "${l.name}": ${l.airSteps} steps off the ground on a grounded leg`);
    if (l.meets && !l.met) failures.push(`lap ${l.lap} "${l.name}": never met ${l.meets}`);
  }
  return { seed, simSeconds: runtime.time - t0, laps: lap, legs, climbed, failures };
}

/** One input the monkey holds. */
interface MonkeyInput {
  kind: "move" | "jump" | "glide" | "idle";
  dir: { x: number; z: number };
  tilt: number;
  sprint: boolean;
}

export interface MonkeyReport {
  seed: number;
  simSeconds: number;
  bursts: number;
  /** Mode changes seen, by "from>to". */
  modes: Record<string, number>;
}

/** Run the seeded monkey for `minutes` of sim time from the hub. */
export function runMonkey(rig: ValleyRig, runtime: Runtime, o: { seed?: number; minutes?: number } = {}): MonkeyReport {
  const seed = o.seed ?? 4242;
  const dir = (r: () => number): { x: number; z: number } => {
    const a = r() * Math.PI * 2;
    return { x: Math.cos(a), z: Math.sin(a) };
  };
  const monkey = createMonkey<MonkeyInput>(
    seed,
    [
      { weight: 5, make: (r) => ({ kind: "move", dir: dir(r), tilt: 0.3 + 0.7 * r(), sprint: r() < 0.3 }) },
      { weight: 2, make: (r) => ({ kind: "jump", dir: dir(r), tilt: r(), sprint: r() < 0.3 }) },
      { weight: 1, make: (r) => ({ kind: "glide", dir: dir(r), tilt: r(), sprint: false }) },
      { weight: 1, make: () => ({ kind: "idle", dir: { x: 0, z: 0 }, tilt: 0, sprint: false }) },
    ],
    { minHold: 0.2, maxHold: 1.5 },
  );
  const before = { ...rig.counts };
  rig.warpHero(SPAWN.x, rig.terrainAt(SPAWN.x, SPAWN.z), SPAWN.z, SPAWN.yaw);
  const t0 = runtime.time;
  let current: MonkeyInput | null = null;
  let held = 0;
  rig.drive = () => {
    const m = monkey.next(rig.dt);
    if (m !== current) {
      current = m;
      held = 0;
      if (m.kind === "jump" || m.kind === "glide") rig.actions.holdAction("jump", 0.25);
    }
    held += rig.dt;
    // a glide: jump, then press again in the air and hold
    if (m.kind === "glide" && Math.abs(held - 0.45) < rig.dt / 2) rig.actions.holdAction("glide", 1.5);
    rig.actions.setStick("move", stickFor(m.dir, rig.control().cameraYaw, m.tilt));
    if (m.sprint) rig.actions.holdAction("sprint", rig.dt * 1.5);
  };
  try {
    advance(runtime, (o.minutes ?? 3) * 60);
  } finally {
    rig.drive = null;
    rig.actions.setStick("move", null);
  }
  const modes: Record<string, number> = {};
  for (const [k, n] of Object.entries(rig.counts)) if (n > (before[k] ?? 0)) modes[k] = n - (before[k] ?? 0);
  return { seed, simSeconds: runtime.time - t0, bursts: monkey.bursts, modes };
}
